import React, { useEffect, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { pickCollectionNode, resolveProofCollection } from "@utils/cmsCollections"
import { getPrototypePageSlice } from "@utils/prototypeSlices"
import IndustriesFiltersSection from "@organisms/sections/IndustriesFiltersSection"
import IndustriesGridSection from "@organisms/sections/IndustriesGridSection"

const prototypeSlice = getPrototypePageSlice("industries")
const industriesExtraStyles = `${prototypeSlice.extraStyles}

.tkr-wrap {
  border-bottom: 0 !important;
}

.ind-filters {
  border-bottom: 0 !important;
}

.cms-page .ind-chip,
.cms-page .ind-chip.is-active,
.cms-page .ind-chip:active,
.cms-page .ind-chip:focus,
.cms-page .ind-chip:focus-visible {
  background-clip: padding-box;
  box-shadow: none !important;
}

.cms-page .show-tab,
.cms-page .show-tab.is-active,
.cms-page .show-tab:active,
.cms-page .show-tab:focus,
.cms-page .show-tab:focus-visible {
  background-clip: padding-box;
  box-shadow: none !important;
}

.cms-page .ind-grid .ind {
  opacity: 1 !important;
  transform: none !important;
}

.cms-page .ind-grid .ind.hidden {
  display: none !important;
}

.cms-page .ind .stat-n {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cms-page .show-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: var(--teal-bg);
  color: var(--teal-dk);
  border: 1px solid var(--teal-lt);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin: 0 auto 16px;
}

.cms-page .show-eyebrow::before {
  content: "";
  width: 6px;
  height: 6px;
  background: var(--teal);
  border-radius: 50%;
  flex: 0 0 auto;
}

.cms-page .cta-final .btn {
  border-radius: 8px;
}

@media (max-width: 960px) {
  .cms-page .hero,
  .cms-page .hero-inner,
  .cms-page .ind-filters,
  .cms-page .ind-grid-sec,
  .cms-page .ind,
  .cms-page .show-head,
  .cms-page .case-inner,
  .cms-page .cta-final,
  .cms-page .mc-section__head,
  .cms-page .cms-page__section-head {
    text-align: center;
  }

  .cms-page .hero-inner,
  .cms-page .show-head,
  .cms-page .case-inner,
  .cms-page .cta-final {
    justify-items: center;
  }

  .cms-page .eyebrow,
  .cms-page .show-eyebrow,
  .cms-page .mc-section__eyebrow,
  .cms-page .cms-page__eyebrow {
    display: inline-flex;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .hero h1,
  .cms-page .show-head h2,
  .cms-page .case-inner h2,
  .cms-page .cta-final h2,
  .cms-page .mc-section__heading,
  .cms-page .cms-page__section-title {
    text-align: center;
  }

  .cms-page .hero .lede,
  .cms-page .show-head p,
  .cms-page .case-inner .section-sub,
  .cms-page .cta-final p,
  .cms-page .mc-section__summary,
  .cms-page .cms-page__section-copy {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  .cms-page .case-sec .case cite {
    justify-content: center;
    text-align: left;
  }

  .cms-page .ind-filters {
    justify-content: center;
  }

  .cms-page .ind .top,
  .cms-page .ind .benefits {
    justify-content: center;
  }

  .cms-page .ind .top {
    align-items: center;
  }

  .cms-page .show-slide .copy .mini-stats {
    align-items: center;
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .cms-page .show-slide .copy .mini-stats .ms,
  .cms-page .show-slide .copy .mini-stats .ms b,
  .cms-page .show-slide .copy .mini-stats .ms span {
    text-align: center;
  }

  .cms-page .cta-final > div,
  .cms-page .mc-section__actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .cms-page .cta-final .btn,
  .cms-page .mc-section__actions .btn {
    width: 100%;
  }
}

@media (max-width: 1024px) {
  .cms-page .case-sec {
    overflow: hidden;
  }

  .cms-page .case-sec .case-inner {
    width: 100%;
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
  }

  .cms-page .case-sec .cases {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 18px !important;
    grid-template-columns: none !important;
    grid-auto-flow: row !important;
    width: 100% !important;
    max-width: 720px;
    margin-left: auto;
    margin-right: auto;
    overflow: visible;
  }

  .cms-page .case-sec .case,
  .cms-page .case-sec .case:nth-child(n) {
    position: relative !important;
    inset: auto !important;
    transform: none !important;
    translate: none !important;
    grid-column: auto !important;
    grid-row: auto !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: none !important;
    min-height: 0 !important;
    height: auto !important;
    margin: 0 !important;
  }

  .cms-page .case-sec .case blockquote {
    min-height: 0 !important;
    height: auto !important;
    margin-left: auto;
    margin-right: auto;
  }
}
`

const heroHeadingHtml = "Built for <em>the way</em> you get paid."
const foundingYear = 2012
const yearsSinceFounding = Math.max(1, new Date().getFullYear() - foundingYear)
const heroSummary =
  `MoonClerk isn't just an all-purpose billing tool that happens to work for your vertical. We've spent ${yearsSinceFounding} years learning how nonprofits, coaches, SaaS founders, and membership orgs actually collect money — and the product reflects it.`

const defaultIndustryFilters = [
  { label: "All industries", filterKey: "all", active: true },
  { label: "Recurring revenue", filterKey: "recurring" },
  { label: "One-time & giving", filterKey: "giving" },
  { label: "Events & enrollment", filterKey: "enrollment" },
  { label: "Selling services", filterKey: "services" },
]

const defaultIndustryCards = [
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M12 21c-4.5-4-8-7.5-8-12a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 4.5-3.5 8-8 12z"/></svg>',
    title: "Nonprofits",
    tag: "Donation forms & recurring giving",
    blurb:
      "Branded donation pages, recurring giving, pay-what-you-want, and branded donation receipts sent automatically. No engineering required.",
    filterCategories: "giving recurring",
    statValue: "85%",
    statLabel: "of nonprofits see their first sustainer within 24 hours",
    benefitsText: "Sustainer giving||Branded receipts||Tribute gifts",
    ctaLabel: "Explore nonprofits",
    anchorLink: "/nonprofits/",
  },
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M17 11V7a5 5 0 0 0-10 0v4"/><rect x="5" y="11" width="14" height="10" rx="2"/></svg>',
    title: "Coaches & consultants",
    tag: "Retainers, packages, cohorts",
    blurb:
      "Sell retainers, course cohorts, VIP days, and package deals from a single link. Auto-invoice, auto-renew, auto-remind — so you can focus on the work.",
    filterCategories: "services recurring enrollment",
    statValue: "$4.2k",
    statLabel: "avg. package size — up from $1.8k on invoice-only tools",
    benefitsText: "Payment plans||Cohort starts||VIP-day flows",
    ctaLabel: "Explore coaching",
    anchorLink: "/coaches/",
  },
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/></svg>',
    title: "SaaS & software",
    tag: "Subscription billing that scales",
    blurb:
      "Trials, seats, usage, proration, dunning — all the billing logic without a billing engineer. For founders who want to ship features, not plumbing.",
    filterCategories: "recurring",
    statValue: "2.1x",
    statLabel: "recovery rate vs. Chargebee — same customers, better dunning",
    benefitsText: "Per-seat billing||Smart dunning||MRR & churn",
    ctaLabel: "Explore SaaS",
    // QA 2026-08-03: label promises examples, so it must lead to examples.
    // These four verticals have no dedicated page; /testimonials carries the
    // matching customer stories and its own category filters.
    anchorLink: "/testimonials/",
  },
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18"/></svg>',
    title: "Membership orgs",
    tag: "Dues, tiers, renewals",
    blurb:
      "Annual dues, tiered memberships, family plans, auto-renewal reminders. For clubs, associations, yoga studios, and congregations of any size.",
    filterCategories: "recurring",
    statValue: "93%",
    statLabel: "of members auto-renew when reminders are handled for you",
    benefitsText: "Annual dues||Tier upgrades||Auto-renewal",
    ctaLabel: "Explore memberships",
    // QA 2026-08-03: label promises examples, so it must lead to examples.
    // These four verticals have no dedicated page; /testimonials carries the
    // matching customer stories and its own category filters.
    anchorLink: "/testimonials/",
  },
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M3 9l9-7 9 7v12a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z"/></svg>',
    title: "Schools & education",
    tag: "Tuition, enrollment, camps",
    blurb:
      "Tuition plans with payment schedules, camp registration, sibling discounts, financial-aid flows. Works for single tutors and districts alike.",
    filterCategories: "enrollment services",
    statValue: "42%",
    statLabel: "faster camp enrollment vs. paper forms + manual invoices",
    benefitsText: "Tuition plans||Sibling discounts||Camp enrollment",
    ctaLabel: "Explore education",
    // QA 2026-08-03: label promises examples, so it must lead to examples.
    // These four verticals have no dedicated page; /testimonials carries the
    // matching customer stories and its own category filters.
    anchorLink: "/testimonials/",
  },
  {
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"><path d="M3 9V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4"/><path d="M2 9h20v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/><path d="M8 13h8"/></svg>',
    title: "Creators & community",
    tag: "Patreon alternative",
    blurb:
      "Member-only content, tier-based access, supporter memberships — without platform fees or losing the customer relationship to a middleman.",
    filterCategories: "giving enrollment services",
    statValue: "0%",
    statLabel: "platform fees — you keep 100% minus Stripe's processing",
    benefitsText: "Tiered supporters||0% platform fees||Own your list",
    ctaLabel: "Explore creators",
    // QA 2026-08-03: label promises examples, so it must lead to examples.
    // These four verticals have no dedicated page; /testimonials carries the
    // matching customer stories and its own category filters.
    anchorLink: "/testimonials/",
  },
]

const defaultIndustryTestimonials = [
  {
    industry: "Nonprofit",
    quote:
      '"We set up a recurring giving form on a Tuesday morning and had our first sustainer donation before <em>lunch.</em> It used to take our dev two weeks to change the donation page color."',
    initials: "MA",
    name: "Meredith Alvarez",
    role: "Dev director · Meridian Arts Foundation",
  },
  {
    industry: "Coaching",
    quote:
      '"I run a 12-week cohort four times a year. MoonClerk handles payment plans, reminders, and dunning. I <em>don\'t touch it.</em> It\'s like having a part-time bookkeeper for $18."',
    initials: "KS",
    name: "Kai Stenberg",
    role: "Founder · Stenberg Leadership Coaching",
  },
  {
    industry: "SaaS",
    quote:
      '"We migrated from Chargebee and the <em>recovery rate doubled.</em> Same customers, same Stripe — just better dunning logic and branded emails. It paid for itself immediately."',
    initials: "JP",
    name: "Jordan Park",
    role: "COO · Briefly (B2B SaaS)",
  },
]

const defaultIndustryLogoBadges = [
  { label: "Meridian Arts" },
  { label: "HARBOR YOGA", variant: "sans" },
  { label: "Rise25" },
  { label: "BRIEFLY", variant: "sans" },
  { label: "Stenberg Coaching" },
  { label: "NORTHPOINT", variant: "sans" },
  { label: "Harvest Table" },
  { label: "OAKRIDGE ACADEMY", variant: "sans" },
]

const IndustriesPage = ({ data }) => {
  const page = data.allDatoCmsMcV2IndustryHubPage.nodes[0] || {}
  const sectionCollection =
    page.sectionCollection ||
    pickCollectionNode(data.allDatoCmsMcV2SectionCollection.nodes, null, "industries-sections")
  const marketCollection =
    page.marketCollection ||
    pickCollectionNode(data.allDatoCmsMcV2MarketCollection.nodes, null, "industries-markets")
  const proofCollection =
    page.proofCollection ||
    resolveProofCollection(data.allDatoCmsMcV2ProofCollection.nodes, null, "industries-proof")
  const hasCanonicalContent = Boolean(page.contentHtml)
  const contentSections = (sectionCollection.contentSections || []).filter(
    section =>
      !["industries_section_01", "industries_hero", "industries_filters", "industries_grid"].includes(
        section.sectionKey
      )
  )
  const industryFilters =
    Array.isArray(marketCollection.marketFilterEntries) && marketCollection.marketFilterEntries.length > 0
      ? marketCollection.marketFilterEntries
      : defaultIndustryFilters
  const industryCards =
    Array.isArray(marketCollection.marketCardEntries) && marketCollection.marketCardEntries.length > 0
      ? marketCollection.marketCardEntries
      : defaultIndustryCards
  const logoBadges =
    Array.isArray(marketCollection.logoBadgeEntries) && marketCollection.logoBadgeEntries.length > 0
      ? marketCollection.logoBadgeEntries
      : defaultIndustryLogoBadges
  const testimonials =
    Array.isArray(proofCollection.testimonials) && proofCollection.testimonials.length > 0
      ? proofCollection.testimonials
      : defaultIndustryTestimonials
  const filterKeys = useMemo(
    () =>
      industryFilters
        .map(item => item.filterKey || item.filter_key || "all")
        .filter(Boolean),
    [industryFilters]
  )
  const initialFilter = filterKeys[0] || "all"
  const [activeIndustryFilter, setActiveIndustryFilter] = useState(initialFilter)
  const pauseAutoFilterUntilRef = useRef(0)

  useEffect(() => {
    setActiveIndustryFilter(currentFilter =>
      filterKeys.includes(currentFilter) ? currentFilter : initialFilter
    )
  }, [filterKeys, initialFilter])

  useEffect(() => {
    if (filterKeys.length < 2) return undefined

    const timer = setInterval(() => {
      if (document.hidden || Date.now() < pauseAutoFilterUntilRef.current) return

      setActiveIndustryFilter(currentFilter => {
        const currentIndex = Math.max(0, filterKeys.indexOf(currentFilter))
        return filterKeys[(currentIndex + 1) % filterKeys.length]
      })
    }, 3200)

    return () => clearInterval(timer)
  }, [filterKeys])

  const handleIndustryFilterChange = filterKey => {
    pauseAutoFilterUntilRef.current = Date.now() + 8000
    setActiveIndustryFilter(filterKey || "all")
  }

  const industryPickerContext = { industryFilters, industryCards }
  const heroSlot = (
    <>
      <section className="hero">
        <div className="hero-inner">
          <span className="eyebrow">Industries we serve</span>
          <h1 dangerouslySetInnerHTML={{ __html: heroHeadingHtml }} />
          <p className="lede">{heroSummary}</p>
        </div>
      </section>
      <IndustriesFiltersSection
        section={{ sectionKey: "industries_filters" }}
        pageContext={industryPickerContext}
        activeFilter={activeIndustryFilter}
        onFilterChange={handleIndustryFilterChange}
      />
      <IndustriesGridSection
        section={{ sectionKey: "industries_grid" }}
        pageContext={industryPickerContext}
        activeFilter={activeIndustryFilter}
      />
    </>
  )

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      heroSlot={heroSlot}
      eyebrow={page.eyebrow}
      heading={page.heading}
      summary={page.summary}
      body={page.body}
      bodyHeading={page.bodyHeading || "Industry Fit"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      contentSections={contentSections}
      industryFilters={[]}
      industryCards={[]}
      testimonials={hasCanonicalContent ? [] : testimonials}
      logoStripEyebrow={hasCanonicalContent ? "" : marketCollection.logoStripEyebrow || ""}
      logoBadges={hasCanonicalContent ? [] : logoBadges}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={industriesExtraStyles}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the industries record in mc_v2_industry_hub_page."
    />
  )
}

export default IndustriesPage

export const query = graphql`
  query IndustriesCmsPageQuery {
    allDatoCmsMcV2IndustryHubPage(filter: { slug: { eq: "industries" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
          }
        }
        marketCollection {
          logoStripEyebrow
          marketFilterEntries {
            label
            filterKey
            active
          }
          marketCardEntries {
            icon
            title
            tag
            blurb
            filterCategories
            statValue
            statLabel
            benefitsText
            ctaLabel
            anchorLink
          }
          logoBadgeEntries {
            label
          }
        }
        proofCollection {
          sourceCollection {
            key
          }
          selectedProfileEntries {
            quote
            name
            initials
            role
            industry
            stat
            logoSvg
            gradient
          }
          selectedTestimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
          testimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
        }
      }
    }
    allDatoCmsMcV2SectionCollection {
      nodes {
        key
        contentSections {
          ...CmsHtmlSectionFields
        }
      }
    }
    allDatoCmsMcV2MarketCollection {
      nodes {
        key
        logoStripEyebrow
        marketFilterEntries {
          label
          filterKey
          active
        }
        marketCardEntries {
          icon
          title
          tag
          blurb
          filterCategories
          statValue
          statLabel
          benefitsText
          ctaLabel
          anchorLink
        }
        logoBadgeEntries {
          label
        }
      }
    }
    allDatoCmsMcV2ProofCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedProfileEntries {
          quote
          name
          initials
          role
          industry
          stat
          logoSvg
          gradient
        }
        selectedTestimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
        testimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
      }
    }
  }
`
